import React, { useEffect, useState } from "react";
import { FaEye, FaRegHeart } from "react-icons/fa";
import { RiShoppingCartLine } from "react-icons/ri";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { add_to_card, add_to_wishlist, messageClear } from "../../store/reducers/cardReducer";
import toast from "react-hot-toast";
import useGuestId from "../../hooks/useGuestId";
import { Dialog, DialogContent, DialogTitle, DialogActions, Button, Box, Typography } from "@mui/material";
import { useTheme } from "../../context/ThemeContext";
import { motion } from "framer-motion";

const FeatureProducts = ({ products }) => {
  const dispatch = useDispatch();
  const guestId = useGuestId();
  const { userInfo } = useSelector(state => state.auth);
  const { errorMessage, successMessage } = useSelector(state => state.card);

  const [quantity] = useState(1);
  const [openSizeDialog, setOpenSizeDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedVariant, setSelectedVariant] = useState(null);

  const { getBackground, getBorder, getMuiButtonStyle, getMuiTextStyle, getMuiBackground, getColor } = useTheme();

  // Función específica para el círculo de color con múltiples colores
  const getVariantColorBackground = colorCode => {
    if (!colorCode) return "#ccc";
    const colors = colorCode.split(",");

    if (colors.length === 1) return colors[0];
    if (colors.length === 2) return `linear-gradient(to right, ${colors.join(", ")})`;
    if (colors.length === 3) return `linear-gradient(to right, ${colors.join(", ")})`;
    return "#ccc";
  };

  // Aplanar el array de productos
  const flattenedProducts = products && products.length > 0 ? (Array.isArray(products[0]) ? products.flat() : products) : [];

  const getMainImage = product => {
    if (!product || !product.variants || product.variants.length === 0) {
      return product?.images?.length > 0 ? product.images[0] : null;
    }
    const v = product.variants[0];
    if (v.colorImages?.length > 0) return v.colorImages[0];
    const tallaConStock = v.sizes?.find(s => s.stock > 0);
    if (tallaConStock?.image) return tallaConStock.image;
    return product.images?.length > 0 ? product.images[0] : null;
  };

  // Segunda imagen para swap al hover (regla de oro)
  const getSecondImage = product => {
    if (!product) return null;
    if (product.variants?.length > 0) {
      const v = product.variants[0];
      if (v.colorImages?.length > 1) return v.colorImages[1];
    }
    if (product.images?.length > 1) return product.images[1];
    return null;
  };

  // Regla de oro precios: con variante → talla con stock; sin variante → precio directo
  const getPriceInfo = product => {
    if (!product) return { original: 0, display: 0, hasDiscount: false, pct: 0 };
    if (product.variants?.length > 0) {
      const sizes = product.variants[0]?.sizes || [];
      const size = sizes.find(s => s.stock > 0) || sizes[0];
      if (size) {
        const hasDiscount = size.discount > 0 && size.discount < size.price;
        const display = hasDiscount ? size.discount : size.price || 0;
        const original = size.price || 0;
        return { original, display, hasDiscount, pct: hasDiscount ? Math.round(((original - display) / original) * 100) : 0 };
      }
    }
    const hasDiscount = product.discount > 0 && product.discount < product.price;
    const display = hasDiscount ? product.discount : product.price || 0;
    const original = product.price || 0;
    return { original, display, hasDiscount, pct: hasDiscount ? Math.round(((original - display) / original) * 100) : 0 };
  };

  const add_card = product => {
    if (product.variants && product.variants.length > 0) {
      setSelectedProduct(product);
      setSelectedVariant(product.variants[0]);
      setOpenSizeDialog(true);
      return;
    }

    const payload = {
      quantity,
      productId: product._id,
      variant: null,
    };

    if (userInfo) {
      payload.userId = userInfo.id;
    } else if (guestId) {
      payload.userId = guestId;
    }
    dispatch(add_to_card(payload));
  };

  const handleAddToCartWithSize = () => {
    if (!selectedSize) {
      alert("Por favor selecciona una talla");
      return;
    }

    const sizeObj = selectedVariant.sizes.find(s => s.size === selectedSize);

    if (!sizeObj || sizeObj.stock <= 0) {
      alert("No hay suficiente stock para esta talla");
      return;
    }

    const variantString = JSON.stringify({
      color: selectedVariant.color,
      colorCode: selectedVariant.colorCode,
      size: sizeObj.size,
      sizeId: sizeObj._id,
      price: sizeObj.price,
      discount: sizeObj.discount,
      sku: sizeObj.sku,
    });

    const payload = {
      productId: selectedProduct._id,
      quantity: 1,
      variant: variantString,
    };

    if (userInfo) {
      payload.userId = userInfo.id;
    } else if (guestId) {
      payload.userId = guestId;
    }

    dispatch(add_to_card(payload));
    setOpenSizeDialog(false);
    setSelectedVariant("");
    setSelectedSize("");
  };

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
  }, [successMessage, errorMessage, dispatch]);

  const add_wishlist = pro => {
    dispatch(
      add_to_wishlist({
        userId: userInfo && userInfo.id ? userInfo.id : "",
        productId: pro._id,
        name: pro.name,
        price: pro.price,
        image: pro.images[0],
        discount: pro.discount,
        rating: pro.rating,
        slug: pro.slug,
      })
    );
  };

  return (
    <div className="w-[85%] flex flex-wrap mx-auto">
      <div className="w-full">
        <div className="text-center flex justify-center items-center flex-col text-4xl text-slate-600 font-bold relative pb-[45px]">
          <h2 className="text-3xl text-slate-600 font-bold">LO NUEVO</h2>
          <div style={{ ...getBackground("primary") }} className="w-[100px] h-[2px] mt-4 mx-auto"></div>
        </div>
      </div>

      <div className="w-full grid grid-cols-4 md:grid-cols-2 sm:grid-cols-1 gap-6">
        {flattenedProducts.map((p, i) => {
          const { original, display, hasDiscount, pct } = getPriceInfo(p);
          const mainImg   = getMainImage(p);
          const secondImg = getSecondImage(p);
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: i * 0.04 }}
              whileHover={{ y: -4, boxShadow: "0 12px 32px 0 rgba(174,29,34,0.18), 0 4px 12px 0 rgba(0,0,0,0.10)" }}
              className="border rounded-lg overflow-hidden cursor-pointer"
              style={{ ...getBackground("surface"), ...getBorder("border") }}>

              <div className="relative overflow-hidden group">
                {/* Badge descuento */}
                {hasDiscount && (
                  <div className="flex justify-center items-center absolute text-white w-[38px] h-[38px] rounded-full font-semibold text-xs left-2 top-2 z-10" style={getBackground("danger")}>
                    -{pct}%
                  </div>
                )}

                <Link to={`/product/details/${p.slug}`} className="block relative h-full" aria-label={`Ver detalles de ${p.name}`}>
                  {/* Imagen principal */}
                  <img
                    className="w-full h-full sm:h-auto object-contain md:object-cover transition-opacity duration-200 group-hover:opacity-0"
                    src={mainImg}
                    alt={p.name}
                    onError={e => { e.target.src = "/images/placeholder-image.jpg"; }}
                  />
                  {/* Segunda imagen swap */}
                  {secondImg && (
                    <img
                      className="absolute top-0 left-0 w-full h-full object-contain opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      src={secondImg}
                      alt={`${p.name} - vista 2`}
                      aria-hidden="true"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  )}
                </Link>

                {/* Botones flotantes - CSS puro */}
                <ul className="flex justify-center items-center gap-2 absolute bottom-3 w-full z-10 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-200">
                  <li onClick={() => add_wishlist(p)} className="w-[38px] h-[38px] cursor-pointer bg-white flex justify-center items-center rounded-full hover:bg-[#FF3A59] hover:text-white hover:rotate-[720deg] transition-all">
                    <FaRegHeart />
                  </li>
                  <Link to={`/product/details/${p.slug}`} className="w-[38px] h-[38px] cursor-pointer bg-white flex justify-center items-center rounded-full hover:bg-[#FF3A59] hover:text-white hover:rotate-[720deg] transition-all">
                    <FaEye />
                  </Link>
                  <li onClick={() => add_card(p)} className="w-[38px] h-[38px] cursor-pointer bg-white flex justify-center items-center rounded-full hover:bg-[#FF3A59] hover:text-white hover:rotate-[720deg] transition-all">
                    <RiShoppingCartLine />
                  </li>
                </ul>
              </div>

              <div className="py-3 px-2">
                <p style={getMuiTextStyle("textdescriptionProduct")} className="font-bold md:text-85 text-sm line-clamp-2 normal-case">
                  {p.name}
                </p>
                <div className="flex items-center gap-3 mt-1">
                  <span style={getMuiTextStyle("textdiscountProduct")} className="font-system-ui text-md font-semibold">
                    S/{display.toFixed(2)}
                  </span>
                  {hasDiscount && (
                    <span style={getMuiTextStyle("textpriceProduct")} className="text-xs line-through">
                      S/{original.toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Diálogo para selección de tallas */}
      <Dialog
        open={openSizeDialog}
        onClose={() => {
          setSelectedVariant("");
          setSelectedSize("");
          setOpenSizeDialog(false);
        }}
        PaperProps={{
          sx: {
            ...getMuiBackground("surface"),
            minWidth: "400px",
            borderRadius: "8px",
          },
        }}>
        <DialogTitle sx={getMuiTextStyle("textdescriptionProduct")}>Selecciona tu talla</DialogTitle>

        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mr: 1, ...getMuiTextStyle("textdescriptionProduct") }}>
              Color:
            </Typography>
            <Box
              sx={{
                width: 20,
                height: 20,
                background: getVariantColorBackground(selectedVariant?.colorCode),
                border: `1px solid ${getColor("border", "#dee2e6")}`,
                borderRadius: "50%",
                display: "inline-block",
              }}
            />
            <Typography variant="body1" sx={{ ml: 1, ...getMuiTextStyle("textdescriptionProduct") }}>
              {selectedVariant?.color || "Color no disponible"}
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, ...getMuiTextStyle("textdescriptionProduct") }}>
              Tallas disponibles
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {selectedVariant?.sizes?.map(sizeObj => {
                const isOutOfStock = sizeObj.stock <= 0;
                const isSelected = selectedSize === sizeObj.size;

                return (
                  <Button
                    key={sizeObj._id}
                    variant={isSelected ? "contained" : "outlined"}
                    onClick={() => !isOutOfStock && setSelectedSize(sizeObj.size)}
                    disabled={isOutOfStock}
                    sx={{
                      minWidth: 0,
                      width: 40,
                      height: 40,
                      position: "relative",
                      fontSize: "0.875rem",
                      fontWeight: isSelected ? 600 : 400,
                      // Aplicar estilos del theme
                      ...(isSelected && getMuiButtonStyle("primary", "contained")),
                      ...(!isSelected && !isOutOfStock && getMuiButtonStyle("primary", "outlined")),
                      // Estilos para deshabilitado
                      ...(isOutOfStock && {
                        color: getColor("textSecondary", "#6c757d"),
                        borderColor: getColor("border", "#dee2e6"),
                        backgroundColor: getColor("surface", "#f8f9fa"),
                        "&:hover": {
                          backgroundColor: getColor("surface", "#f8f9fa"),
                        },
                      }),
                    }}
                    aria-label={`Talla ${sizeObj.size}${isOutOfStock ? " - Agotado" : ""}`}>
                    {sizeObj.size}
                    {isOutOfStock && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          width: "80%",
                          height: 1,
                          backgroundColor: getColor("danger", "#dc3545"),
                          transform: "translate(-50%, -50%) rotate(45deg)",
                        }}
                      />
                    )}
                  </Button>
                );
              })}
            </Box>
            {!selectedSize && (
              <Typography
                variant="caption"
                sx={{
                  mt: 1,
                  display: "block",
                  color: getColor("danger", "#dc3545"),
                }}>
                Por favor selecciona una talla
              </Typography>
            )}
          </Box>

          {selectedSize &&
            (() => {
              const sizeInfo = selectedVariant.sizes.find(s => s.size === selectedSize);
              return (
                <Box sx={{ mt: 2 }}>
                  {sizeInfo?.discount > 0 ? (
                    <>
                      <Typography
                        variant="h6"
                        sx={{
                          textDecoration: "line-through",
                          color: getColor("textSecondary", "#6c757d"),
                        }}>
                        S/ {sizeInfo.price.toFixed(2)}
                      </Typography>

                      <Typography variant="h6" sx={{ color: getColor("textdiscountProduct", "#28a745") }}>
                        S/ {sizeInfo.discount.toFixed(2)}
                        <Box
                          component="span"
                          sx={{
                            ml: 1,
                            fontSize: "0.8rem",
                            color: getColor("textpercentdsctProduct", "#28a745"),
                          }}>
                          ({Math.round(((sizeInfo.price - sizeInfo.discount) / sizeInfo.price) * 100)}%)
                        </Box>
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="h6" sx={getMuiTextStyle("textpriceProduct")}>
                      S/ {sizeInfo?.price.toFixed(2)}
                    </Typography>
                  )}
                </Box>
              );
            })()}
        </DialogContent>

        <DialogActions
          sx={{
            ...getMuiBackground("text:"),
            p: 2,
            gap: 1,
          }}>
          <Button onClick={() => setOpenSizeDialog(false)} sx={getMuiButtonStyle("secondary", "outlined")}>
            Cancelar
          </Button>
          <Button onClick={handleAddToCartWithSize} variant="contained" disabled={!selectedSize} sx={getMuiButtonStyle("primary", "contained")}>
            Agregar al carrito
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FeatureProducts;
